import React, { forwardRef } from 'react'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Chip,
} from '@mui/material'
import Grid from '@mui/material/Grid2'
import {
  VolunteerActivism,
  EmojiEvents,
  Star,
  WorkspacePremium,
} from '@mui/icons-material'
import type { ContributionTier } from '@/types/donate'

export interface ContributionTiersSectionProps {
  /**
   * Section title
   */
  title: string

  /**
   * Optional introduction text
   */
  intro?: string

  /**
   * Array of contribution tiers to display
   */
  tiers: ContributionTier[]

  /**
   * Callback when tier card is clicked
   */
  // eslint-disable-next-line no-unused-vars
  onTierClick: (tierId: string) => void

  /**
   * Optional callback when tier card is viewed/hovered (for analytics)
   */
  // eslint-disable-next-line no-unused-vars
  onTierView?: (tierId: string) => void
}

/**
 * Map icon names to Material-UI icon components
 */
const iconComponents: Record<string, React.ElementType> = {
  VolunteerActivism,
  EmojiEvents,
  Star,
  WorkspacePremium,
}

/**
 * Contribution tiers section component
 * Displays donor recognition levels with benefits and CTA buttons
 */
export const ContributionTiersSection = forwardRef<
  HTMLElement,
  ContributionTiersSectionProps
>(({ title, intro, tiers, onTierClick, onTierView }, ref) => {
  /**
   * Handle tier card hover for analytics tracking
   */
  const handleTierHover = (id: string) => {
    if (onTierView) {
      onTierView(id)
    }
  }

  return (
    <Box
      ref={ref}
      component="section"
      id="contribution-tiers-section"
      sx={{
        py: { xs: 6, sm: 8, md: 10 },
        bgcolor: 'background.default',
      }}
      aria-labelledby="tiers-title"
    >
      <Container maxWidth="lg">
        <Stack spacing={{ xs: 4, sm: 5, md: 6 }}>
          {/* Section Header */}
          <Box textAlign="center">
            <Typography
              id="tiers-title"
              variant="h2"
              component="h2"
              sx={{
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                fontWeight: 700,
                color: 'text.primary',
                mb: 2,
              }}
            >
              {title}
            </Typography>
            {intro && (
              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: '1rem', sm: '1.0625rem', md: '1.125rem' },
                  color: 'text.secondary',
                  maxWidth: '700px',
                  mx: 'auto',
                }}
              >
                {intro}
              </Typography>
            )}
          </Box>

          {/* Tier Cards */}
          <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
            {tiers.map(tier => {
              const IconComponent = tier.icon ? iconComponents[tier.icon] : null

              return (
                <Grid key={tier.id} size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card
                    onMouseEnter={() => handleTierHover(tier.id)}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                      borderWidth: tier.featured ? 2 : 1,
                      borderStyle: 'solid',
                      borderColor: tier.featured ? 'primary.main' : 'divider',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4,
                      },
                    }}
                    aria-label={`Contribution tier: ${tier.name}, $${tier.minAmount} or more`}
                  >
                    {tier.featured && (
                      <Chip
                        label="Limited Time"
                        color="primary"
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 16,
                          right: 16,
                          fontWeight: 600,
                        }}
                      />
                    )}

                    <CardContent
                      sx={{
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        p: { xs: 2, sm: 3 },
                      }}
                    >
                      {/* Icon */}
                      {IconComponent && (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            mb: 2,
                          }}
                        >
                          <IconComponent
                            sx={{
                              fontSize: 48,
                              color: tier.featured
                                ? 'primary.main'
                                : 'text.secondary',
                            }}
                          />
                        </Box>
                      )}

                      {/* Tier Name */}
                      <Typography
                        variant="h3"
                        component="h3"
                        sx={{
                          fontSize: { xs: '1.25rem', sm: '1.5rem' },
                          fontWeight: 700,
                          color: 'text.primary',
                          textAlign: 'center',
                          mb: 1,
                        }}
                      >
                        {tier.name}
                      </Typography>

                      {/* Minimum Amount */}
                      <Typography
                        variant="h4"
                        component="p"
                        sx={{
                          fontSize: { xs: '1.5rem', sm: '1.75rem' },
                          fontWeight: 700,
                          color: 'primary.main',
                          textAlign: 'center',
                          mb: 2,
                        }}
                      >
                        ${tier.minAmount}+
                      </Typography>

                      {/* Description */}
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          textAlign: 'center',
                          mb: 3,
                          fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                        }}
                      >
                        {tier.description}
                      </Typography>

                      {/* Benefits List */}
                      <Stack spacing={1} sx={{ mb: 3, flexGrow: 1 }}>
                        {tier.benefits.map((benefit, index) => (
                          <Typography
                            key={index}
                            variant="body2"
                            sx={{
                              fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                              color: 'text.primary',
                              display: 'flex',
                              alignItems: 'flex-start',
                              '&::before': {
                                content: '"✓"',
                                color: 'primary.main',
                                fontWeight: 'bold',
                                mr: 1,
                                flexShrink: 0,
                              },
                            }}
                          >
                            {benefit}
                          </Typography>
                        ))}
                      </Stack>

                      {/* CTA Button */}
                      <Button
                        variant={tier.featured ? 'contained' : 'outlined'}
                        color="primary"
                        fullWidth
                        size="large"
                        onClick={() => onTierClick(tier.id)}
                        aria-label={`Donate at ${tier.name} level, $${tier.minAmount} or more`}
                        sx={{
                          py: 1.5,
                          fontSize: { xs: '0.9375rem', sm: '1rem' },
                          fontWeight: 600,
                          minHeight: 44, // Accessibility touch target
                        }}
                      >
                        Contribute Now
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              )
            })}
          </Grid>
        </Stack>
      </Container>
    </Box>
  )
})

ContributionTiersSection.displayName = 'ContributionTiersSection'
